import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { GlobalEventsService } from '@app/global-events.service';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import {
  Credentials,
  CredentialsService,
} from '@core/services/credentials.service';
import { AuthService } from '@core/services/auth.service';
import { RefreshTokenResponse } from '@core/models/refresh-token.model';
import { SendOtpResponse, VerifyOtpResponse } from '@core/models/otp.model';

const routes = {
  sendOtp: () => `/org/sgs/register/otps`,
  verifyOtp: () => `/org/sgs/register/otp-verifications`,
  sendEmailOtp: () => `/org/sgs/register/otps-email`,
  verifyEmailOtp: () => `/org/sgs/register/otp-email-verifications`,
  sendSmsOtp: () => `/org/sgs/register/otps-sms`,
  verifySmsOtp: () => `/org/sgs/register/otp-sms-verifications`,
  register: () => `/org/sgs/register`,
  createCompany: () => `/org/merchant/company/create`,
  getTermsAndConditions: () => `/org/sgs/lookup/legal`,
  sendVerification: () => `/org/sgs/reset-password/send-verification`,
  verifyEmail: () => `/org/sgs/reset-password/verify`,
  resetPassword: () => `/org/sgs/reset-password/reset-password`,
  logout: () => '/org/sgs/profile/logout',
  reactivateRequest: () => '/org/sgs/auth/reactivate-request',
  reactivateConfirm: () => '/org/sgs/auth/reactivate-confirm',
};

/** Recover Password takes exactly one of email or phone number */
export type PasswordRecoveryIdentifier =
  | { email: string; phoneNumber?: never }
  | { phoneNumber: string; email?: never };

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}

/** Shape returned by the `/org/token` endpoint (non-Azure auth path). */
interface RawLoginResponse {
  accessToken: string;
  expireAt: number;
  refreshToken: string;
  isPasswordResetRequired: boolean;
}

export interface LoginResponse {
  access_token: string;
  expire_at: number;
  refresh_token: string;
  is_password_reset_required: boolean;
  // Not actually populated by the login endpoint today (see LoginComponent's
  // saveCredentials, which reads this optimistically) — kept optional so
  // that pre-existing behaviour (companyName ends up undefined) is preserved.
  companyName?: string;
}

export interface CompanyDetails {
  id: string;
  statusId: number;
  companyName?: string;
}

export interface UserPhoneNumber {
  phoneNumber: string;
}

export interface LegalDocument {
  link: string;
  version: number;
}

export interface TermsAndConditionsResponse {
  privacyPolicy: LegalDocument;
  termsAndConditions: LegalDocument;
}

/**
 * Data collected across the registration wizard and posted to the
 * `register` endpoint. Optional/index-signature fields reflect that this
 * object is assembled and passed between components dynamically.
 */
export interface RegisterAccountData {
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  password: string;
  terms?: unknown;
  [key: string]: unknown;
}

export interface VerifyEmailResponse {
  token: string;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private _token_refreshed = false;
  set token_refreshed(flag: boolean) {
    this._token_refreshed = flag;
  }
  constructor(
    private credentialsService: CredentialsService,
    private httpClient: HttpClient,
    private globalEventsService: GlobalEventsService,
    private router: Router,
    private authService: AuthService,
  ) {}

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(context: LoginContext): Observable<LoginResponse> {
    const url = '/org/token';
    if (environment.authType == 'Azure') {
      const body = new URLSearchParams();
      body.set('username', context.username);
      body.set('password', context.password);
      body.set('grant_type', 'password');
      body.set('response_type', 'token id_token');

      body.set('client_id', environment.activeDirectoryClientId);
      body.set('scope', environment.activeDirectoryScope);

      // NOTE: the Azure AD auth flow is legacy/unused (see AuthenticationGuard,
      // whose MSAL check is commented out); its real response shape differs
      // from LoginResponse, but this keeps the method's declared type honest
      // for the active (non-Azure) path without altering this branch's behavior.
      return this.httpClient.post<LoginResponse>(url, body.toString(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      });
    } else {
      const body = {
        username: context.username,
        password: context.password,
        client_id: environment.activeDirectoryClientId,
      };

      return this.httpClient
        .post<RawLoginResponse>(url, body, {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        })
        .pipe(
          map((data): LoginResponse => {
            return {
              access_token: data.accessToken,
              expire_at: data.expireAt,
              refresh_token: data.refreshToken,
              is_password_reset_required: data.isPasswordResetRequired,
            };
          }),
        );
    }
  }

  refreshTokenAsync() {
    if (!this._token_refreshed) {
      // Share the interceptor's in-flight refresh: refresh tokens are
      // single-use, so a second parallel refresh would end the session.
      // AuthService logs out if the server rejects the session; on a
      // transient failure keep the user in and retry on the next navigation.
      return this.authService.refreshTokenAsync().pipe(
        map((data) => {
          this._token_refreshed = true;
          return !!data;
        }),
        catchError(() => of(this.credentialsService.isAuthenticated())),
      );
    }

    return of(true);
  }

  private createRefreshTokenRequest(): Observable<RefreshTokenResponse> {
    return this.authService.createRefreshTokenRequest();
  }

  /**
   * @deprecated Use refreshTokenAsync instead.
   */
  refreshToken() {
    const refreshTokenReques = this.createRefreshTokenRequest();
    refreshTokenReques.subscribe({
      next: (res: RefreshTokenResponse) => {
        this.updateRefreshedToken(res);
        // this.loadCompanyInformation();
      },
      error: (err: HttpErrorResponse) => {},
    });
  }

  setCredentials(data: Credentials) {
    this.credentialsService.setCredentials(data, true);
  }

  private loadCompanyInformation() {
    this.getCompanyDetails().subscribe({
      next: (res) => {
        if (res && res.length > 0) {
          const _credentials = this.credentialsService.credentials;
          _credentials.companyId = res[0].id;
          this.credentialsService.setCredentials(_credentials);
          if (res[0].statusId === 1) {
            this.router.navigateByUrl('/companyReview');
          } else if (res[0].statusId === 2) {
            this.router.navigateByUrl('/companyReview/rejected');
          }
        }
        if (res.length === 0) {
          const _credentials = this.credentialsService.credentials;
          this.credentialsService.setCredentials(_credentials);
          this.router.navigateByUrl('/createCompany');
        }
      },
      error: (err: HttpErrorResponse) => {},
    });
  }

  private updateRefreshedToken(res: RefreshTokenResponse) {
    return this.authService.updateRefreshedToken(res);
  }

  getCompanyDetails(): Observable<CompanyDetails[]> {
    // TODO: move this to the appropriate place
    // load company plan
    this.globalEventsService.onLoadCompanyPlan.emit();

    return this.httpClient.get<CompanyDetails[]>('/org/merchant/Company/Get', {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  getUserAllPhoneNumbers(accessToken: string): Observable<UserPhoneNumber[]> {
    return this.httpClient.get<UserPhoneNumber[]>(
      '/org/profile/Profile/GetAllPhones',
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        }),
      },
    );
  }

  getTermsAndConditions(): Observable<TermsAndConditionsResponse> {
    return this.httpClient.get<TermsAndConditionsResponse>(
      routes.getTermsAndConditions(),
    );
  }

  sendOtp(
    accessToken: string,
    phone: string,
    email?: string,
  ): Observable<SendOtpResponse> {
    return this.httpClient.post<SendOtpResponse>(
      routes.sendOtp(),
      {
        phoneNumber: phone,
        email,
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        }),
      },
    );
  }

  verifyOtp(
    accessToken: string,
    code: string,
    phoneNumber: string,
  ): Observable<VerifyOtpResponse> {
    return this.httpClient.post<VerifyOtpResponse>(
      routes.verifyOtp(),
      {
        phoneNumber,
        otp: code,
        valid: true,
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        }),
      },
    );
  }

  sendEmailOtp(data: { email: string }): Observable<SendOtpResponse> {
    return this.httpClient.post<SendOtpResponse>(routes.sendEmailOtp(), {
      email: data.email,
    });
  }

  verifyEmailOtp(data: {
    email: string;
    otp: string;
  }): Observable<VerifyOtpResponse> {
    return this.httpClient.post<VerifyOtpResponse>(routes.verifyEmailOtp(), {
      email: data.email,
      otp: data.otp,
      valid: true,
    });
  }

  sendSmsOtp(data: { phone: string }): Observable<SendOtpResponse> {
    return this.httpClient.post<SendOtpResponse>(routes.sendSmsOtp(), {
      phoneNumber: data.phone,
    });
  }

  verifySmsOtp(data: {
    phone: string;
    otp: string;
  }): Observable<VerifyOtpResponse> {
    return this.httpClient.post<VerifyOtpResponse>(routes.verifySmsOtp(), {
      phoneNumber: data.phone,
      otp: data.otp,
      valid: true,
    });
  }
  createProfile(
    account: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      password: string;
      dateofBirthUnix: number;
      acceptedTncVersion: number;
    },
    token: string,
  ): Observable<unknown> {
    return this.httpClient.post(routes.register(), account, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
      }),
    });
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */

  logout(): Observable<boolean | Object> {
    return this.httpClient
      .post(
        routes.logout(),
        {},
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          }),
        },
      )
      .pipe(
        catchError(() => of(null)), // ignore API errors
        switchMap(() => this.authService.logout()), // always logout user
        finalize(() => this.authService.logout()), // safety net
      );
  }

  register(account: RegisterAccountData, token: string): Observable<unknown> {
    return this.httpClient.post(routes.register(), account, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
      }),
    });
  }

  createCompany(
    companyData: Record<string, unknown>,
    vat: File,
    tradeLicense: File,
  ): Observable<unknown> {
    const formData = new FormData();
    formData.append('request', JSON.stringify(companyData));
    formData.append('vat', vat);
    formData.append('tradelicense', tradeLicense);
    return this.httpClient.post(routes.createCompany(), formData, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  sendVerification(
    data: PasswordRecoveryIdentifier,
  ): Observable<SendOtpResponse> {
    return this.httpClient.post<SendOtpResponse>(
      routes.sendVerification(),
      data,
    );
  }

  verifyEmail(
    data: PasswordRecoveryIdentifier & { code: string },
  ): Observable<VerifyEmailResponse> {
    return this.httpClient.post<VerifyEmailResponse>(
      routes.verifyEmail(),
      data,
    );
  }

  resetPassword(
    data: { password: string },
    passwordResetToken: string,
  ): Observable<unknown> {
    return this.httpClient.post(routes.resetPassword(), data, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${passwordResetToken}`,
      }),
    });
  }

  reactivateRequest(email: string): Observable<SendOtpResponse> {
    return this.httpClient.post<SendOtpResponse>(routes.reactivateRequest(), {
      email,
    });
  }

  reactivateConfirm(email: string, otpCode: string): Observable<unknown> {
    return this.httpClient.post(routes.reactivateConfirm(), {
      email,
      otpCode,
    });
  }
}
